import { useApiResponse } from '@/composables/useApiResponse';
import { ONE_MINUTE_IN_MS } from '@/constants';
import { CreditNoteProcessAutomatedStatus } from '@/generated';
import { AutomaticProcessingStatus } from '@/models/automaticProcessingStatus';
import useAuthStore from '@/stores/useAuthStore';
import useSignalRStore from '@/stores/useSignalRStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';

import { creditNoteApi } from './generatedApi';

export const QUERY_AUTOMATIC_PROCESSING_PAUSED = 'automatic-processing-paused';
export const AUTOMATIC_PROCESSING_PAUSED_QUERY_KEY = [QUERY_AUTOMATIC_PROCESSING_PAUSED] as const;

export const useAutomaticProcessingPaused = () => {
    const apiResponseCode = useApiResponse();
    const { isAuthenticated, isAdmin } = storeToRefs(useAuthStore());
    const { setAutomaticProcessingStatus } = useSignalRStore();

    const query = useQuery({
        queryKey: AUTOMATIC_PROCESSING_PAUSED_QUERY_KEY,
        queryFn: async () => {
            const response = await creditNoteApi.apiV1CreditnoteProcessAutomatedStatusGet();

            if (!apiResponseCode(response.status).ok) {
                throw new Error(
                    `Could not load automatic processing status (status: ${response.status})`
                );
            }

            return response.data.status === CreditNoteProcessAutomatedStatus.Paused;
        },
        enabled: computed(() => isAuthenticated.value && isAdmin.value),
        staleTime: ONE_MINUTE_IN_MS,
    });

    watch(
        query.data,
        isPaused => {
            if (isPaused !== undefined) {
                setAutomaticProcessingStatus(
                    isPaused ? AutomaticProcessingStatus.Paused : AutomaticProcessingStatus.Active
                );
            }
        },
        { immediate: true }
    );

    return query;
};

export const useAutomaticProcessingPausedActions = () => {
    const apiResponseCode = useApiResponse();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: AUTOMATIC_PROCESSING_PAUSED_QUERY_KEY,
        mutationFn: async (isPaused: boolean): Promise<void> => {
            const status = isPaused
                ? CreditNoteProcessAutomatedStatus.Paused
                : CreditNoteProcessAutomatedStatus.Active;
            const response = await creditNoteApi.apiV1CreditnoteProcessAutomatedStatusPut({
                status,
            });

            if (!apiResponseCode(response.status).ok) {
                throw new Error(
                    `Could not set automatic processing status (status: ${response.status})`
                );
            }
        },
        onSuccess: (_, isPaused) => {
            queryClient.setQueryData<boolean>(AUTOMATIC_PROCESSING_PAUSED_QUERY_KEY, isPaused);
        },
    });
};
